import clsx from "clsx";
import { BookOpen, Loader2 } from "lucide-react";
import { FC, useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Progress } from "~/components/ui/progress";
import { MAX_FILE_SIZE_BYTES } from "~/features/settings/components/reports/importExportUtils";
import { useToast } from "~/hooks/use-toast";

const ACCEPTED_KNOWLEDGE_TYPES = {
  "application/pdf": [".pdf"],
  "text/markdown": [".md", ".markdown"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/msword": [".doc"],
};

interface FtueUploadKnowledgeProps {
  isUploading: boolean;
  uploadProgress: number;
  onUpload: (file: File) => Promise<void>;
  onSkip: () => void;
}

export const FtueUploadKnowledge: FC<FtueUploadKnowledgeProps> = ({
  isUploading,
  uploadProgress,
  onUpload,
  onSkip,
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const [isDragActive, setIsDragActive] = useState(false);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "importTemplate.fileTooLarge",
            defaultMessage: "File size exceeds the maximum limit of 25MB",
          }),
        });
        return;
      }

      await onUpload(file);
    },
    [intl, onUpload, toast]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: (acceptedFiles, fileRejections) => {
      setIsDragActive(false);
      if (fileRejections.length > 0) {
        toast({
          variant: "destructive",
          title: intl.formatMessage({
            id: "ftue.uploadKnowledge.invalidFile",
            defaultMessage: "Only PDF, Word, and Markdown files are allowed",
          }),
        });
        return;
      }
      handleDrop(acceptedFiles);
    },
    accept: ACCEPTED_KNOWLEDGE_TYPES,
    multiple: false,
    disabled: isUploading,
  });

  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
      <div className="max-w-xl w-full text-center space-y-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-foreground">
            <FormattedMessage
              id="ftue.uploadKnowledge.title"
              defaultMessage="Add to Your Knowledge Base"
            />
          </h1>
          <p className="text-lg text-muted-foreground">
            <FormattedMessage
              id="ftue.uploadKnowledge.subtitle"
              defaultMessage="Upload guidelines, standards, or reference materials — your reports will be more accurate and tailored to your practice."
            />
          </p>
        </div>

        <Card
          {...getRootProps()}
          className={clsx("cursor-pointer transition-all", {
            "border-purple-300 bg-purple-50/50 dark:border-purple-700 dark:bg-purple-950/50":
              isUploading,
            "border-purple-500 border-dashed shadow-md bg-purple-50/30 dark:bg-purple-950/30":
              isDragActive && !isUploading,
            "hover:border-purple-500 hover:shadow-md":
              !isUploading && !isDragActive,
          })}
        >
          <input {...getInputProps()} />
          <CardHeader className="text-center py-10">
            {isUploading ? (
              <>
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                </div>
                <CardTitle className="text-lg text-purple-700 dark:text-purple-300">
                  <FormattedMessage
                    id="ftue.uploadKnowledge.uploading"
                    defaultMessage="Uploading your document..."
                  />
                </CardTitle>
                <div className="mt-4 px-8">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-sm text-muted-foreground mt-2">
                    {uploadProgress}%
                  </p>
                </div>
              </>
            ) : (
              <>
                <div
                  className={clsx(
                    "mx-auto mb-4 w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center",
                    { "opacity-50": isDragActive }
                  )}
                >
                  <BookOpen className="w-8 h-8 text-purple-600" />
                </div>
                <CardTitle className="text-lg">
                  <FormattedMessage
                    id="ftue.uploadKnowledge.dropzone"
                    defaultMessage="Click to upload a knowledge document"
                  />
                </CardTitle>
                <CardDescription className="mt-2">
                  <FormattedMessage
                    id="ftue.uploadKnowledge.formats"
                    defaultMessage="Supports PDF, Word, Markdown, and text files (max 25MB)"
                  />
                </CardDescription>
              </>
            )}
          </CardHeader>
        </Card>

        <div className="flex justify-center gap-4">
          <Button
            variant="link"
            onClick={onSkip}
            disabled={isUploading}
            className="text-muted-foreground hover:text-foreground"
          >
            <FormattedMessage
              id="ftue.uploadKnowledge.skip"
              defaultMessage="Skip for Now"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
